import * as React from "react";
import ReactLazyLoad from "react-lazyload";
import { QNProps as QNParams } from "@ezbuy/smartimagesrc/lib/ImageSrcHandlers/Handler";
import Image from "next/image";
import { imageSourceGenerator, transformImageTights } from "utils/image";
import { getRuntime } from "utils/runtime";

interface Status {
	originSrc: string;
	currentSrc: string;
}
export interface SmartImageProps {
	/** 是否为测试环境 测试环境和生产使用的 CDN 域名不同 仅对 ezbuy 有效 */
	online?: boolean;
	qnParams?: QNParams;
	isLazyLoad?: boolean;
	src: string;
	placeholder?: string;
	fallbackSrc?: string | false;
	aliSize?: number;
	keepSize?: boolean;
	imgClass?: string;
	linkClass?: string;
	link?: string;
	style?: any;
	title?: string;
	alt?: string;
	blankLink?: boolean;
	/** 加载完毕 无论最终结果是成功或者失败 */
	onFinish?: (status: Status) => void;
	moduleIndex?: number;
	moduleInnerIndex?: number;
	layoutFill?: boolean;
}

interface ImageState {
	useOriginSrc: boolean;
	imageSrc: string;
}

interface ImageSize {
	width: number | string;
	height: number | string;
}

interface ImageLayout {
	layout: "fill";
}

const dataImageRegex = /^data:image/;

export class NextImage extends React.Component<SmartImageProps, ImageState> {
	static defaultProps = {
		online: true,
		className: "",
		isLazyLoad: false,
		alisize: 0,
		placeholder:
			"https://litb-cgis.rightinthebox.com/imagegy/202101/6bd45737852e4852b57d009ef352d098wecomtemp156206c4feeea45299e0167cf834862d8c943.png",
		blankLink: false,
	};
	get imageProps() {
		const { qnParams, aliSize, online, keepSize } = this.props;
		const { isSupportWebp } = getRuntime();
		return {
			qnParams,
			aliSize,
			keepSize: !!keepSize,
			online: !!online,
			isSupportWebp,
		};
	}

	get fallbackSrc() {
		return this.props.fallbackSrc;
	}

	imageSourceGenerator: any = imageSourceGenerator(
		this.props.src,
		this.imageProps,
		this.fallbackSrc,
	);

	componentWillReceiveProps(nextProps: SmartImageProps) {
		if (this.props.src !== nextProps.src) {
			this.imageSourceGenerator = imageSourceGenerator(
				nextProps.src,
				this.imageProps,
				this.fallbackSrc,
			);
			this.updateImageSrc(nextProps);
		}
	}

	public state: ImageState;

	constructor(props) {
		super(props);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { callback: _, ...rest } = this.genImageInfo(props);
		this.state = {
			useOriginSrc: false,
			imageSrc: "",
			...rest,
		};
	}

	genImageInfo = (src?: string) => {
		if (!src) {
			return {};
		}
		if (dataImageRegex.test(src)) {
			return { src };
		}
		const r = this.imageSourceGenerator.next();
		return {
			useOriginSrc: r.done,
			imageSrc: r.value,
			callback: () => {
				r.done && this.finish();
			},
		};
	};

	updateImageSrc = (props = this.props) => {
		const { callback, ...rest } = this.genImageInfo(props.src);
		this.setState(
			{
				...this.state,
				...rest,
			},
			() => typeof callback === "function" && callback(),
		);
	};

	finish() {
		const { onFinish, src } = this.props;
		if (typeof onFinish === "function") {
			onFinish({
				originSrc: src,
				currentSrc: this.state.imageSrc,
			});
		}
	}

	onImageLoad = (event: any) => {
		try {
			if (event.target.naturalWidth <= 1) {
				return this.onImageFailedLoad();
			}
			this.finish();
		} catch (err) {}
	};

	onImageFailedLoad = () => {
		if (!this.state.useOriginSrc) {
			this.updateImageSrc();
		}
	};

	nextImgLoad = ({ src, width }) => {
		return src.includes("?")
			? src.endsWith("&")
				? `${src}w=${width}`
				: `${src}&w=${width}`
			: `${src}?w=${width}`;
	};

	render() {
		const {
			isLazyLoad,
			placeholder,
			imgClass,
			link,
			linkClass,
			title,
			alt,
			style,
			blankLink,
			moduleIndex,
			moduleInnerIndex = 0,
			layoutFill,
		} = this.props;
		let { src } = this.props;

		src = this.state.imageSrc || src;
		src = transformImageTights(src);
		const { height, width } = style;
		const imageSize: ImageSize | ImageLayout =
			height && width && !layoutFill ? { height, width } : { layout: "fill" };
		const priority = moduleIndex < 3 && moduleInnerIndex < 1;
		const imgProps = {
			className: imgClass,
			title,
			alt,
			loader: this.nextImgLoad,
			...imageSize,
			priority,
		};
		const placeholderEle = <Image src={placeholder} {...imgProps} />;
		const aProps = link && blankLink ? { target: "_blank" } : {};
		if (!src) {
			return link ? (
				<a href={link} className={linkClass} {...aProps}>
					<Image src={placeholder} {...imgProps} />
				</a>
			) : (
				<Image src={placeholder} {...imgProps} />
			);
		}

		let ele;
		if (isLazyLoad) {
			ele = (
				<ReactLazyLoad height={height} placeholder={placeholderEle}>
					<Image
						onLoad={this.onImageLoad}
						onError={this.onImageFailedLoad}
						src={src}
						{...imgProps}
						data-load-type="lazy"
					/>
				</ReactLazyLoad>
			);
		} else {
			ele = (
				<Image
					src={src}
					onLoad={this.onImageLoad}
					onError={this.onImageFailedLoad}
					{...imgProps}
					data-load-type="normal"
				/>
			);
		}

		if (link) {
			return (
				<a href={link} className={linkClass}>
					{ele}
				</a>
			);
		}
		return ele;
	}
}

export default NextImage;
